/**
 * Årsstripe for artikkelplanen — tolv månedsrader med merkedagene plassert.
 *
 * Poenget er dekningshull: måneder uten planlagt innhold vises med stiplet
 * plassholder, så «har jeg noe i august?» besvares på et blikk. `computeRunwayMonths`
 * regner ut det samme som et tall; dette gjør det romlig.
 *
 * Vertikal, ikke horisontal tidslinje — 20 merkedager på en bred akse blir uleselig
 * i en admin-kolonne. Se docs/plan-gjenstaende-gap.md Spor F.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import type { Article, ArticleWheelEntry } from '../../../types';
import {
  NORSKE_MANEDER, occasionDate, ymdUTC, formatRelativeDays, daysBetweenYmd,
} from '../../../utils/articleWheelDates';

interface Props {
  entries: ArticleWheelEntry[];
  /** «YYYY-MM-DD» — regnes som i dag. */
  today: string;
  planHorizonYmd?: string;
  /** Klikk på en merkedag (åpner redigering i årshjul-panelet). */
  onSelect?: (entryId: string) => void;
  selectedId?: string | null;
  /** Artikler, for å koble en merkedag til det den faktisk produserte. */
  articles?: Article[];
  /** Åpne artikkelen en merkedag har produsert. */
  onOpenArticle?: (a: Article) => void;
}

/** Kort status på artikkelen en merkedag produserte. */
const PRODUCED_SHORT: Record<string, string> = {
  published: 'Ute',
  approved: 'Planlagt',
  pending_review: 'Venter',
  draft: 'Utkast',
  generating: 'Lages',
};
const PRODUCED_LABEL: Record<string, string> = {
  published: 'Publisert',
  approved: 'Godkjent',
  pending_review: 'Til godkjenning',
  draft: 'Utkast',
  generating: 'Genereres',
};
const PRODUCED_TONE: Record<string, string> = {
  published: 'bg-emerald-500/15 text-emerald-400',
  approved: 'bg-emerald-500/10 text-emerald-400/80',
  pending_review: 'bg-emerald-500/20 text-emerald-300',
  draft: 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)]',
  generating: 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)]',
};

interface MonthRow {
  month: number;
  label: string;
  isCurrent: boolean;
  isPast: boolean;
  entries: { entry: ArticleWheelEntry; ymd: string; done: boolean }[];
}

/** Fordel merkedagene på månedsrader for et gitt år. */
export function buildMonthRows(entries: ArticleWheelEntry[], today: string, year?: number): MonthRow[] {
  const todayYear = Number(today.slice(0, 4));
  const planYear = year ?? todayYear;
  const isThisYear = planYear === todayYear;
  const currentMonth = Number(today.slice(5, 7));

  const rows: MonthRow[] = NORSKE_MANEDER.map((name, i) => ({
    month: i + 1,
    label: name.slice(0, 3),
    isCurrent: isThisYear && i + 1 === currentMonth,
    // Et framtidig år har ingen passerte måneder; et tidligere år har bare passerte.
    isPast: planYear < todayYear || (isThisYear && i + 1 < currentMonth),
    entries: [],
  }));

  for (const entry of entries || []) {
    if (!entry?.enabled) continue;
    const ymd = ymdUTC(occasionDate(entry.recurrence, planYear));
    const m = Number(ymd.slice(5, 7));
    if (m >= 1 && m <= 12) {
      rows[m - 1].entries.push({ entry, ymd, done: ymd < today });
    }
  }

  for (const r of rows) r.entries.sort((a, b) => a.ymd.localeCompare(b.ymd));
  return rows;
}

const ArticleYearStrip: React.FC<Props> = ({
  entries, today, planHorizonYmd, onSelect, selectedId, articles, onOpenArticle,
}) => {
  const [year, setYear] = React.useState(() => Number(today.slice(0, 4)));
  const rows = React.useMemo(() => buildMonthRows(entries, today, year), [entries, today, year]);
  const gaps = rows.filter((r) => r.entries.length === 0).length;

  // Koblingen merkedag → produsert artikkel finnes allerede via wheel_entry_id;
  // uten den er årsstripa en plan uten forbindelse til resultatet.
  const byWheelId = React.useMemo(() => {
    const map = new Map<string, Article>();
    for (const a of articles || []) {
      if (a.wheel_entry_id && !map.has(a.wheel_entry_id)) map.set(a.wheel_entry_id, a);
    }
    return map;
  }, [articles]);

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-panel p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">Årshjul</h3>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setYear((y) => y - 1)}
              aria-label="Forrige år"
              className="p-1 rounded-pill text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-bold tabular-nums text-[var(--text-secondary)] w-10 text-center">{year}</span>
            <button
              type="button"
              onClick={() => setYear((y) => y + 1)}
              aria-label="Neste år"
              className="p-1 rounded-pill text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
        <span className="text-xs text-[var(--text-muted)]">
          {planHorizonYmd ? `Dekket til ${planHorizonYmd}` : 'Ingen planhorisont'}
        </span>
      </div>

      <div className="space-y-0.5">
        {rows.map((row) => (
          <div
            key={row.month}
            className={`flex items-start gap-3 py-1.5 px-2 rounded-card transition ${
              row.isCurrent ? 'bg-emerald-500/5' : ''
            }`}
          >
            <span
              className={`w-10 flex-shrink-0 pt-0.5 text-tiny font-black uppercase tracking-widest tabular-nums ${
                row.isCurrent ? 'text-emerald-400' : row.isPast ? 'text-[var(--text-muted)]/50' : 'text-[var(--text-muted)]'
              }`}
            >
              {row.label}
            </span>

            {row.entries.length === 0 ? (
              /* Dekningshull: stiplet plassholder gjør tomme måneder synlige,
                 i stedet for at de bare mangler i en liste. */
              <div
                className="flex-1 h-6 rounded-card border border-dashed border-[var(--border-secondary)]"
                title={`Ingen planlagt innhold i ${NORSKE_MANEDER[row.month - 1]}`}
                aria-label={`${NORSKE_MANEDER[row.month - 1]}: ingen planlagt innhold`}
              />
            ) : (
              <div className="flex-1 min-w-0 space-y-1">
                {row.entries.map(({ entry, ymd, done }) => {
                  const days = daysBetweenYmd(today, ymd);
                  const isSelected = selectedId === entry.id;
                  const produced = byWheelId.get(entry.id);
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => onSelect?.(entry.id)}
                      className={`w-full flex items-center gap-2 text-left px-2 py-1 rounded-card transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
                        isSelected ? 'bg-emerald-500/15' : 'hover:bg-[var(--bg-surface-alt)]'
                      }`}
                    >
                      {/* Passert = hul ring, kommende = fylt. En nedtonet fylt prikk
                          forsvant helt på mørk flate og leste som manglende data. */}
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          done
                            ? 'border border-[var(--text-muted)] bg-transparent'
                            : 'bg-emerald-400'
                        }`}
                      />
                      {/* Dagen i måneden. Uten den er stripa en liste, ikke en kalender. */}
                      <span className="w-6 flex-shrink-0 text-tiny tabular-nums text-[var(--text-muted)] text-right">
                        {Number(ymd.slice(8, 10))}.
                      </span>
                      <span
                        className={`text-xs truncate ${
                          done ? 'text-[var(--text-muted)]' : 'text-[var(--text-secondary)]'
                        }`}
                      >
                        {entry.title}
                      </span>
                      {row.isCurrent && !done && (
                        <span className="flex-shrink-0 text-tiny font-black uppercase tracking-widest text-emerald-400">
                          {formatRelativeDays(days)}
                        </span>
                      )}
                      {/* Koblingen plan → resultat. Uten den er stripa en plan
                          uten forbindelse til det som faktisk ble laget. */}
                      {produced ? (
                        <span
                          role="link"
                          tabIndex={0}
                          title={`${PRODUCED_LABEL[produced.status] || produced.status}: ${produced.title}`}
                          onClick={(ev) => { ev.stopPropagation(); onOpenArticle?.(produced); }}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter' || ev.key === ' ') {
                              ev.preventDefault(); ev.stopPropagation();
                              onOpenArticle?.(produced);
                            }
                          }}
                          className={`ml-auto flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded-pill text-tiny font-black uppercase tracking-widest transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${PRODUCED_TONE[produced.status] || PRODUCED_TONE.draft}`}
                        >
                          <FileText size={10} />
                          {PRODUCED_SHORT[produced.status] || '?'}
                        </span>
                      ) : done ? (
                        /* Passert uten produsert artikkel — et hull i utførelsen,
                           ikke i planen. Verdt å se. */
                        <span
                          className="ml-auto flex-shrink-0 text-tiny text-amber-400/70"
                          title="Merkedagen er passert uten at noe ble produsert"
                        >
                          —
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {gaps > 0 && (
        <p className="text-xs text-[var(--text-muted)] leading-relaxed">
          {gaps === 1 ? 'Én måned' : `${gaps} måneder`} uten planlagt innhold. Legg til en
          merkedag under, eller la crawleren fylle hullet når den er på plass.
        </p>
      )}
    </div>
  );
};

export default ArticleYearStrip;
